import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { FormDomainScriptGuideService } from '../../form-domain-script-guide.service';
import { FormDomainScriptGuidePageAssistService } from './form-domain-script-guide-page-assist.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MonModalService } from 'app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PAGE_ASSIST_SCRIPT_CONSTANT } from '@client/app/forms/domain-script-guide/features/pa-script-settings/pa-script.constant';
import { DOMAIN_SCRIPT_GUIDE_EVENTS } from '@client/app/forms/constants/domain-script-guide';
import {
    PAScriptSettingsComponent,
} from '@client/app/forms/domain-script-guide/features/pa-script-settings/pa-script-settings.component';

@Component({
    selector: 'form-domain-script-guide-features-page-assist',
    standalone: false,
    templateUrl: './page-assist.component.html',
})
export class FormDomainScriptGuideFeaturesPageAssistComponent implements OnInit, OnDestroy {
    @Input() prefixName?: string;
    @Input() domain: any;
    @Input() pageAssistModel: any;
    @Input() supportLegacy: boolean = true;

    settingEnabled: boolean = false;
    firstTimeEnablePA: boolean = false;
    dialogId: any;
    private paSettingsFormIsValid: boolean = true;
    private subscriptions: number[] = [];
    private discardChangesConfirmationText: string;

    constructor (
        private formDomainScriptGuideService: FormDomainScriptGuideService,
        private formDomainScriptGuidePageAssistService: FormDomainScriptGuidePageAssistService,
        private monDialog: MonDialog,
        private monEventsService: MonEventService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
        private cdRef: ChangeDetectorRef,
    ) {
        this.discardChangesConfirmationText = this.translateService.getString(
            'The settings form contains incorrect data. Close the form anyway?',
        );
    }

    ngOnInit () {
        this.activate();
    }

    ngOnDestroy () {
        for (const eventId of this.subscriptions) {
            this.monEventsService.remove(eventId);
        }
    }

    activate () {
        this.firstTimeEnablePA = !(this.pageAssistModel && this.pageAssistModel.settings && this.pageAssistModel.settings.enabled);
        if (typeof this.supportLegacy !== 'boolean') {
            this.supportLegacy = true;
        }
        this.settingEnabled = this.pageAssistModel.getIsEnabled();
        this.pageAssistModel.settings.canSavePaSetting = true;
        if (this.firstTimeEnablePA) {
            Object.assign(this.pageAssistModel.settings, PAGE_ASSIST_SCRIPT_CONSTANT.DEFAULT_SETTINGS);
        }

        const updatePaSettingsSub = this.monEventsService.addListener('updatePaSettings' as any, (paSettings: any) => {
            this.pageAssistModel = Object.assign(this.pageAssistModel, {
                settings: paSettings,
            });
        });

        const paSettingsFormValidStatusSub = this.monEventsService.addListener('paSettingsFormValidStatus' as any, (valid: boolean) => {
            this.paSettingsFormIsValid = valid;
        });

        this.subscriptions.push(updatePaSettingsSub, paSettingsFormValidStatusSub);
    }

    async onEnabledChange (event) {
        await this.monEventsService.run('formUnsavedUpdate' as any);
        await this.monEventsService.run(DOMAIN_SCRIPT_GUIDE_EVENTS.FORM_UNSAVED_UPDATE as any);
        this.settingEnabled = event;
        if (this.pageAssistModel.getVersionState() === 'v1') {
            this.pageAssistModel.settings.enabled = false;
            this.pageAssistModel.legacySettings.enabled = this.settingEnabled;
        } else {
            this.pageAssistModel.settings.enabled = this.settingEnabled;
            this.pageAssistModel.legacySettings.enabled = false;
        }

        this.updateDomainSettings();
        this.cdRef.detectChanges();
    }

    canUseFeature () {
        return this.formDomainScriptGuidePageAssistService.getAccessToFeature(this.domain);
    }

    switchPageAssistSetting (pageAssistVersion: string) {
        this.pageAssistModel.setVersionState(pageAssistVersion);
        if (pageAssistVersion === 'v1') {
            this.pageAssistModel.settings.enabled = false;
            this.pageAssistModel.legacySettings.enabled = this.settingEnabled;
        } else {
            this.pageAssistModel.settings.enabled = this.settingEnabled;
            this.pageAssistModel.legacySettings.enabled = false;
        }
    }

    async openPageAssistScriptSettings () {
        const params = {
            body: PAScriptSettingsComponent,
            size: 'md',
            data: {
                settings: this.pageAssistModel.settings,
            },
        };

        const onBeforeClose = () => new Promise((resolve) => {
            if (this.paSettingsFormIsValid) {
                resolve(true);
            } else {
                this.monModalService.confirm(this.discardChangesConfirmationText).then(() => {
                    resolve(true);
                }, () => {
                    resolve(false);
                });
            }
        });

        this.dialogId = await this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params, onBeforeClose });
    }

    // PROTECTED
    private updateDomainSettings () {
        this.formDomainScriptGuideService.setPageAssistEdit(this.pageAssistModel);
    }
}
